import { db } from "../firebase";
  
  import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    serverTimestamp,
    doc,
    updateDoc,
    Timestamp,
  } from "firebase/firestore";

import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

  import { useBalance } from "../context/BalanceContext";
  
  const TIMEZONE = "Africa/Dar_es_Salaam";
  
  // ==========================================
  // MINIMUM BET
  // ==========================================
  
  const MINIMUMS = {
    TZS: 10000,
    USDT: 5,
    KES: 900,
    UGX: 25500,
    RWF: 10500,
    BIF: 21000,
    ZMW: 135,
    MWK: 14000,
    MZN: 450,
    USD: 7,
    SSP: 42500,
    BWP: 94,
    MGA: 2900,
  };
  
  // ==========================================
  // FIRESTORE BALANCE FIELDS
  // ==========================================
  
  const fieldMap = {
    TZS: "tshBalance",
    USDT: "usdtBalance",
    KES: "KESBalance",
    UGX: "UGXBalance",
    RWF: "RWFBalance",
    BIF: "BIFBalance",
    ZMW: "ZMWBalance",
    MWK: "MWKBalance",
    MZN: "MZNBalance",
    USD: "USDBalance",
    SSP: "SSPBalance",
    BWP: "BWPBalance",
    MGA: "MGABalance",
  };
  
  // ==========================================
  // HELPERS
  // ==========================================
  
  const getNumericOdds = (odds) => {
    if (
      odds === undefined ||
      odds === null ||
      odds === ""
    ) {
      return 0;
    }
  
    const numericValue = Number(
      String(odds)
        .replace("%", "")
        .trim()
    );
  
    if (!Number.isFinite(numericValue)) {
      return 0;
    }
  
    return numericValue;
  };
  
  // ==========================================

  // ==========================================
  // PRIVATE PLANE: LIMIT ONE BET PER TANZANIA DAY
  // ==========================================

  const getTanzaniaDayBounds = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());

    const year = Number(parts.find((part) => part.type === "year")?.value);
    const month = Number(parts.find((part) => part.type === "month")?.value);
    const day = Number(parts.find((part) => part.type === "day")?.value);

    // Tanzania uses UTC+3. Convert Tanzania midnight to UTC.
    const start = new Date(
      Date.UTC(year, month - 1, day) - 3 * 60 * 60 * 1000
    );
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);

    return { start, end };
  };

  const hasPlayedPrivatePlaneToday = async (username) => {
    const { start, end } = getTanzaniaDayBounds();
    const collectionsToCheck = [
      "otherbetantscore",
      "otherbetcorectscore",
    ];

    const snapshots = await Promise.all(
      collectionsToCheck.map((collectionName) =>
        getDocs(
          query(
            collection(db, collectionName),
            where("username", "==", username)
          )
        )
      )
    );

    return snapshots.some((snapshot) =>
      snapshot.docs.some((betDoc) => {
        const bet = betDoc.data();
        const createdAt = bet.createdAt;

        if (!createdAt || typeof createdAt.toDate !== "function") {
          return false;
        }

        const createdAtDate = createdAt.toDate();
        return createdAtDate >= start && createdAtDate < end;
      })
    );
  };

  // MAIN COMPONENT
  // ==========================================
  
  export default function BetPlace() {
    const { state } = useLocation();
    const navigate = useNavigate();
  
    const match = state?.match;
    const selection = state?.selection;
  
    const { setBalances } = useBalance();
  
    const [amount, setAmount] = useState("");
    const [activeCurrency, setActiveCurrency] = useState(null);
    const [firestoreBalances, setFirestoreBalances] = useState({});
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [betSuccess, setBetSuccess] = useState(null);
  
    // ==========================================
    // LOAD USER BALANCES
    // ==========================================
  
    useEffect(() => {
      let isMounted = true;
  
      const loadUserBalances = async () => {
        try {
          setBalanceLoading(true);
  
          const username = localStorage.getItem("username");
  
          if (!username) {
            console.error("Username haipo localStorage");
  
            if (isMounted) {
              setFirestoreBalances({});
            }
  
            return;
          }
  
          const userQuery = query(
            collection(db, "users"),
            where("username", "==", username)
          );
  
          const snapshot = await getDocs(userQuery);
  
          if (snapshot.empty) {
            console.error("User hajapatikana:", username);
  
            if (isMounted) {
              setFirestoreBalances({});
            }
  
            return;
          }
  
          const userData = snapshot.docs[0].data();
          const loadedBalances = {};
  
          Object.entries(fieldMap).forEach(
            ([currency, firebaseField]) => {
              loadedBalances[currency] = Number(
                userData[firebaseField] || 0
              );
            }
          );
  
          if (isMounted) {
            setFirestoreBalances(loadedBalances);
  
            if (typeof setBalances === "function") {
              setBalances((previous) => ({
                ...(previous || {}),
                ...loadedBalances,
              }));
            }
          }
        } catch (error) {
          console.error("Balance load error:", error);
  
          if (isMounted) {
            setFirestoreBalances({});
          }
        } finally {
          if (isMounted) {
            setBalanceLoading(false);
          }
        }
      };
  
      loadUserBalances();
  
      return () => {
        isMounted = false;
      };
    }, [setBalances]);
  
    // ==========================================
    // AVAILABLE CURRENCIES
    // ==========================================
  
    const availableCurrencies = useMemo(() => {
      return Object.entries(firestoreBalances)
        .filter(([, balance]) => Number(balance || 0) > 0)
        .map(([currency, balance]) => ({
          currency,
          balance: Number(balance || 0),
        }));
    }, [firestoreBalances]);
  
    const showCurrencySelect =
      availableCurrencies.length > 1;
  
    // ==========================================
    // DEFAULT CURRENCY
    // ==========================================
  
    useEffect(() => {
      if (balanceLoading) {
        return;
      }
  
      if (availableCurrencies.length === 0) {
        setActiveCurrency(null);
        return;
      }
  
      const currencyStillAvailable =
        availableCurrencies.some(
          (item) => item.currency === activeCurrency
        );
  
      if (!currencyStillAvailable) {
        setActiveCurrency(
          availableCurrencies[0].currency
        );
      }
    }, [
      availableCurrencies,
      activeCurrency,
      balanceLoading,
    ]);
  
    // ==========================================
    // NO BET DATA
    // ==========================================
  
    if (!match || !selection) {
      return (
        <div className="betplace-empty">
          No Bet Data
        </div>
      );
    }
  
    // ==========================================
    // CURRENT BALANCE
    // ==========================================
  
    const currentBalance = activeCurrency
      ? Number(
          firestoreBalances[activeCurrency] || 0
        )
      : 0;
  
    // ==========================================
    // BET MODE
    // ==========================================
  
    const isCorrectScore =
      selection?.mode === "correct";
  
    // ==========================================
    // ODDS FROM BETSCORES
    // ==========================================
  
    /**
     * Correct Score haitumii tena:
     * 25%, 35%, au 50%.
     *
     * Inatumia odds zilizotumwa ndani ya:
     * selection.odds
     */
    const percent = getNumericOdds(
      selection?.odds
    );
  
    // ==========================================
    // CALCULATIONS
    // ==========================================
  
    const numericAmount = Number(amount || 0);
    const numericOdds = Number(percent || 0);
  
    const calculatedProfit =
      numericAmount > 0 && numericOdds > 0
        ? (numericAmount * numericOdds) / 100
        : 0;
  
    const totalWin =
      numericAmount + calculatedProfit;
  
    // ==========================================
    // FORMAT MONEY
    // ==========================================
  
    const formatMoney = (value) => {
      const number = Number(value || 0);
  
      if (!Number.isFinite(number)) {
        return "0.00";
      }
  
      return number.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };
  
    // ==========================================
    // PARSE MATCH DATE
    // ==========================================
  
    const parseMatchDate = (date, time) => {
      if (!date) {
        return null;
      }
  
      if (typeof date?.toDate === "function") {
        return date.toDate();
      }
  
      if (date instanceof Date) {
        return date;
      }
  
      const dateString = String(date).trim();
  
      const timeString = time
        ? String(time).trim()
        : "00:00";
  
      if (
        dateString.includes("T") ||
        dateString.includes(" ")
      ) {
        const existing = new Date(
          dateString.replace(" ", "T")
        );
  
        if (
          Number.isFinite(
            existing.getTime()
          )
        ) {
          return existing;
        }
      }
  
      const eatDateTime = new Date(
        `${dateString}T${timeString}:00+03:00`
      );
  
      if (
        Number.isFinite(
          eatDateTime.getTime()
        )
      ) {
        return eatDateTime;
      }
  
      return null;
    };
  
    const matchDateObj =
      parseMatchDate(
        match.date,
        match.time
      ) || new Date();
  
    // ==========================================
    // FORMAT DATE EAT
    // ==========================================
  
    const formatDateEAT = (date) => {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: TIMEZONE,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    };
  
    // ==========================================
    // FORMAT TIME EAT
    // ==========================================
  
    const formatTimeEAT = (date) => {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date);
    };
  
    const formattedDate =
      formatDateEAT(matchDateObj);
  
    const formattedTime =
      formatTimeEAT(matchDateObj);
  
    // ==========================================
    // QUICK AMOUNTS
    // ==========================================
  
    const getQuickAmounts = () => {
      const minimum =
        MINIMUMS[activeCurrency] || 1;
  
      return [
        minimum * 3,
        minimum * 10,
        minimum * 30,
      ];
    };
  
    // ==========================================
    // BET ID
    // ==========================================
  
    const generateBetId = () => {
      const letters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  
      const numbers =
        "0123456789";
  
      const letterPart = Array.from(
        { length: 4 },
        () =>
          letters.charAt(
            Math.floor(
              Math.random() *
                letters.length
            )
          )
      );
  
      const numberPart = Array.from(
        { length: 16 },
        () =>
          numbers.charAt(
            Math.floor(
              Math.random() *
                numbers.length
            )
          )
      );
  
      const combined = [
        ...letterPart,
        ...numberPart,
      ];
  
      for (
        let i = combined.length - 1;
        i > 0;
        i--
      ) {
        const j = Math.floor(
          Math.random() * (i + 1)
        );
  
        [combined[i], combined[j]] = [
          combined[j],
          combined[i],
        ];
      }
  
      return combined.join("");
    };
  
    // ==========================================
    // TEAMS
    // ==========================================
  
    const teamA =
      match.A ||
      match.teamA ||
      "";
  
    const teamB =
      match.B ||
      match.teamB ||
      "";
  
    // ==========================================
    // HOME TEAM
    // ==========================================
  
    const homeTeam =
      match.homeTeam || "";
  
    const isTeamAHome =
      homeTeam &&
      teamA &&
      String(homeTeam).trim() ===
        String(teamA).trim();
  
    const isTeamBHome =
      homeTeam &&
      teamB &&
      String(homeTeam).trim() ===
        String(teamB).trim();
  
    // ==========================================
    // HANDLE BET
    // ==========================================
  
    const handleBet = async () => {
      if (loading) {
        return;
      }
  
      const betAmount = Number(amount);
  
      // ========================================
      // CURRENCY CHECK
      // ========================================
  
      if (!activeCurrency) {
        alert("No available currency balance");
        return;
      }
  
      // ========================================
      // AMOUNT CHECK
      // ========================================
  
      if (
        !Number.isFinite(betAmount) ||
        betAmount <= 0
      ) {
        alert("Enter valid amount");
        return;
      }
  
      // ========================================
      // ODDS CHECK
      // ========================================
  
      if (
        !Number.isFinite(percent) ||
        percent <= 0
      ) {
        alert(
          "Invalid odds. Please return and select the bet again."
        );
  
        return;
      }
  
      // ========================================
      // MINIMUM BET
      // ========================================
  
      const minimum =
        MINIMUMS[activeCurrency] || 0;
  
      if (betAmount < minimum) {
        alert(
          `Minimum bet for ${activeCurrency} is ${minimum.toLocaleString(
            "en-GB"
          )}`
        );
  
        return;
      }
  
      // ========================================
      // LOCAL BALANCE CHECK
      // ========================================
  
      if (betAmount > currentBalance) {
        alert("Insufficient balance");
        return;
      }
  
      try {
        setLoading(true);
        setShowSpinner(true);
  
        // ======================================
        // USERNAME
        // ======================================
  
        const username =
          localStorage.getItem("username");
  
        if (!username) {
          throw new Error(
            "Username not found"
          );
        }
  
        // ======================================
        // PRIVATE PLANE: CHECK TODAY'S BETS
        // ======================================
        const alreadyPlayed = await hasPlayedPrivatePlaneToday(username);

        if (alreadyPlayed) {
          throw new Error(
            "You have already played Private Plane Games today. Please wait another day."
          );
        }

        // GET FRESH USER DATA
        // ======================================
  
        const userQuery = query(
          collection(db, "users"),
          where("username", "==", username)
        );
  
        const snapshot =
          await getDocs(userQuery);
  
        if (snapshot.empty) {
          throw new Error(
            "User not found"
          );
        }
  
        const userDoc =
          snapshot.docs[0];
  
        const userData =
          userDoc.data();
  
        const userRef = doc(
          db,
          "users",
          userDoc.id
        );
  
        // ======================================
        // KO USER
        // ======================================
  
        const isKOUser =
          userData.KO === "YES";
  
        // ======================================
        // BALANCE FIELD
        // ======================================
  
        const field =
          fieldMap[activeCurrency];
  
        if (!field) {
          throw new Error(
            "Currency field not found"
          );
        }
  
        // ======================================
        // FRESH FIRESTORE BALANCE
        // ======================================
  
        const firebaseBalance =
          Number(
            userData[field] || 0
          );
  
        if (betAmount > firebaseBalance) {
          setFirestoreBalances(
            (previous) => ({
              ...previous,
              [activeCurrency]:
                firebaseBalance,
            })
          );
  
          throw new Error(
            "Insufficient balance"
          );
        }
  
        // ======================================
        // FINAL PERCENT
        // ======================================
  
        /**
         * Correct Score:
         * Inatumia selection.odds kutoka BetScores.
         *
         * Non-Correct Score kwa KO user:
         * Inatumia 25% kama business rule.
         */
        const finalPercent =
          isKOUser && !isCorrectScore
            ? 25
            : Number(percent);
  
        if (
          !Number.isFinite(finalPercent) ||
          finalPercent <= 0
        ) {
          throw new Error(
            "Invalid betting percentage"
          );
        }
  
        // ======================================
        // FINAL PROFIT
        // ======================================
  
        const finalProfit =
          (betAmount * finalPercent) / 100;
  
        const finalTotalWin =
          betAmount + finalProfit;
  
        // ======================================
        // BET ID
        // ======================================
  
        const betId = generateBetId();
  
        // ======================================
        // MATCH DATE OBJECT
        // ======================================
  
        const safeMatchDateObj =
          parseMatchDate(
            match.date,
            match.time
          ) || new Date();
  
        // ======================================
        // MATCH ID
        // ======================================
  
        const matchId =
          match.id ||
          match.sofaId ||
          "";
  
        // ======================================
        // MATCH DATE / TIME
        // ======================================
  
        const savedMatchDate =
          match.date
            ? String(match.date)
            : "";
  
        const savedMatchTime =
          match.time
            ? String(match.time)
            : "";
  
        // ======================================
        // BET DATA
        // ==========================================
  
        const betData = {
          betId,
          username,
          matchId,
  
          teamA,
          teamB,
          homeTeam: homeTeam || "",
  
          league: match.league || "",
          country: match.country || "",
  
          score: selection.score,
          odds: finalPercent,
          mode: selection.mode || "",
  
          amount: betAmount,
          stake: betAmount,
          quantity: betAmount,
  
          profit: finalProfit,
          totalWin: finalTotalWin,
          currency: activeCurrency,
  
          matchDate: savedMatchDate,
          matchTime: savedMatchTime,
  
          matchTimestamp:
            Timestamp.fromDate(
              safeMatchDateObj
            ),
  
          matchDateText:
            savedMatchDate,
  
            type: isCorrectScore
            ? "correctscore"
            : "antscore",
  
          status: "pending",
          createdAt: serverTimestamp(),
        };
  
        // ======================================
        // COLLECTION
        // ======================================
  
        const collectionName = isCorrectScore
        ? "otherbetcorectscore"
        : "otherbetantscore";
        // ======================================
        // SAVE BET
        // ======================================
  
        await addDoc(
          collection(
            db,
            collectionName
          ),
          betData
        );
  
        // ======================================
        // NEW BALANCE
        // ======================================
  
        const newBalance =
          firebaseBalance - betAmount;
  
        // ======================================
        // UPDATE FIRESTORE BALANCE
        // ======================================
  
        await updateDoc(userRef, {
          [field]: newBalance,
        });
  
        // ======================================
        // UPDATE LOCAL BALANCE
        // ======================================
  
        setFirestoreBalances(
          (previous) => ({
            ...previous,
            [activeCurrency]:
              newBalance,
          })
        );
  
        // ======================================
        // UPDATE BALANCE CONTEXT
        // ======================================
  
        if (
          typeof setBalances === "function"
        ) {
          setBalances((previous) => ({
            ...(previous || {}),
  
            [activeCurrency]:
              newBalance,
  
            [field]: newBalance,
          }));
        }
  
        // ======================================
        // SUCCESS DELAY
        // ======================================
  
        await new Promise((resolve) => {
          setTimeout(resolve, 3000);
        });
  
        // ======================================
        // SUCCESS MODAL
        // ======================================
  
        setShowSpinner(false);
  
        setBetSuccess({
          betId,
          amount: betAmount,
          currency: activeCurrency,
          matchDate: safeMatchDateObj,
        });
  
        setLoading(false);
        setAmount("");
      } catch (error) {
        console.error(
          "BET ERROR:",
          error
        );
  
        setShowSpinner(false);
        setLoading(false);
  
        alert(
          error?.message ||
            "Bet failed"
        );
      }
    };
  
    // ==========================================
    // UI
    // ==========================================
  
    return (
      <div className="betplace">
        {/* ======================================
            PROCESSING
        ====================================== */}
  
        {showSpinner && (
          <div className="order-result-overlay">
            <div className="order-result-sheet">
              <div className="order-processing">
                <div className="processing-spinner" />
  
                <h2>Processing...</h2>
  
                <p>Please wait</p>
              </div>
            </div>
          </div>
        )}
  
        {/* ======================================
            SUCCESS
        ====================================== */}
  
        {betSuccess && (
          <div className="order-result-overlay">
            <div className="order-result-sheet">
              <div className="order-success">
                <div className="success-check">
                  ✓
                </div>
  
                <h2>
                  Bet Placed Successfully
                </h2>
  
                <div className="bet-success-info">
                  <div>
                    <span>Amount</span>
  
                    <strong>
                      {betSuccess.currency}{" "}
                      {betSuccess.amount.toLocaleString(
                        "en-GB"
                      )}
                    </strong>
                  </div>
  
                  <div>
                    <span>Bet ID</span>
  
                    <strong>
                      {betSuccess.betId}
                    </strong>
                  </div>
  
                  <div>
                    <span>Match Date</span>
  
                    <strong>
                      {formatDateEAT(
                        betSuccess.matchDate
                      )}
                    </strong>
                  </div>
  
                  <div>
                    <span>
                      Kick-off Time
                    </span>
  
                    <strong>
                      {formatTimeEAT(
                        betSuccess.matchDate
                      )}
                    </strong>
                  </div>
                </div>
  
                <button
                  type="button"
                  className="order-result-ok"
                  onClick={() => {
                    setBetSuccess(null);
                    navigate(-1);
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
  
        {/* ======================================
            BET CARD
        ====================================== */}
  
        <div className="betplace-card">
          <div className="betplace-handle" />
  
          <div className="betplace-you-bet">
            You Bet
          </div>
  
          <div className="betplace-selection-title">
            {isCorrectScore
              ? "Score"
              : "Score"}{" "}
            {selection.score}
          </div>
  
          <div className="betplace-league">
            {"{ "}
  
            {match.country
              ? `${match.country}: `
              : ""}
  
            {match.league || "League"}
  
            {" }"}
          </div>
  
          {/* TEAMS */}
  
          <div className="betplace-teams">
            <div className="betplace-team">
              <div
                className="betplace-football"
                aria-label="Football"
              >
                ⚽
              </div>
  
              <strong>
                {teamA}
              </strong>
  
              {isTeamAHome && (
                <small>
                  (Home)
                </small>
              )}
            </div>
  
            <div className="betplace-vs">
              VS
            </div>
  
            <div className="betplace-team">
              <div
                className="betplace-football"
                aria-label="Football"
              >
                ⚽
              </div>
  
              <strong>
                {teamB}
              </strong>
  
              {isTeamBHome && (
                <small>
                  (Home)
                </small>
              )}
            </div>
          </div>
  
          {/* DATE */}
  
          <div className="betplace-date">
            {formattedDate}{" "}
            {formattedTime}
          </div>
  
          {/* BALANCE LOADING */}
  
          {balanceLoading && (
            <div className="betplace-balance-loading">
              Loading balance...
            </div>
          )}
  
          {/* CALCULATION */}
  
          <div className="betplace-calc">
            <div className="betplace-calc-col">
              <div className="betplace-calc-label">
                Amount
              </div>
  
              <div className="betplace-input-wrap">
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  placeholder="Please fill in the amount"
                  value={amount}
                  onChange={(e) => {
                    setAmount(
                      e.target.value
                    );
                  }}
                />
  
                {showCurrencySelect ? (
                  <select
                    className="betplace-currency-select"
                    value={
                      activeCurrency || ""
                    }
                    onChange={(e) => {
                      setActiveCurrency(
                        e.target.value
                      );
  
                      setAmount("");
                    }}
                  >
                    {availableCurrencies.map(
                      (item) => (
                        <option
                          key={item.currency}
                          value={item.currency}
                        >
                          {item.currency}
                        </option>
                      )
                    )}
                  </select>
                ) : (
                  <div className="betplace-single-currency">
                    {activeCurrency || "-"}
                  </div>
                )}
              </div>
            </div>
  
            {/* PROFIT */}
  
            <div className="betplace-calc-col betplace-profit-col">
              <div className="betplace-calc-label">
                Profit
              </div>
  
              <div className="betplace-profit-value">
                × {numericOdds}%
              </div>
  
              <small>
                {activeCurrency || ""}{" "}
                {formatMoney(
                  calculatedProfit
                )}
              </small>
            </div>
  
            {/* GAIN */}
  
            <div className="betplace-calc-col">
              <div className="betplace-calc-label">
                Gain
              </div>
  
              <div className="betplace-gain-value">
                {activeCurrency || "-"}{" "}
                {formatMoney(totalWin)}
              </div>
            </div>
          </div>
  
          {/* QUICK AMOUNTS */}
  
          <div className="betplace-quick">
            {getQuickAmounts().map(
              (quickAmount) => (
                <button
                  type="button"
                  key={quickAmount}
                  disabled={
                    !activeCurrency ||
                    balanceLoading
                  }
                  onClick={() => {
                    setAmount(
                      String(quickAmount)
                    );
                  }}
                >
                  +
                  {quickAmount.toLocaleString(
                    "en-GB"
                  )}
                </button>
              )
            )}
  
            <button
              type="button"
              className="betplace-all"
              disabled={
                !activeCurrency ||
                balanceLoading
              }
              onClick={() => {
                setAmount(
                  String(currentBalance)
                );
              }}
            >
              ALL
            </button>
  
            <button
              type="button"
              className="betplace-settings"
              title="Amount settings"
              onClick={() => {
                setAmount("");
              }}
            >
              ⚙️
            </button>
          </div>
  
          {/* BALANCE */}
  
          <div className="betplace-balance">
            <span>
              Balance
            </span>
  
            <strong>
              {balanceLoading
                ? "Loading..."
                : `${activeCurrency || "-"} ${formatMoney(
                    currentBalance
                  )}`}
            </strong>
          </div>
  
          {/* NO BALANCE */}
  
          {!balanceLoading &&
            availableCurrencies.length === 0 && (
              <div className="betplace-no-balance">
                No available balance
              </div>
            )}
  
          {/* ACTIONS */}
  
          <div className="betplace-actions">
            <button
              type="button"
              className="betplace-cancel"
              disabled={loading}
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </button>
  
            <button
              type="button"
              className="betplace-confirm"
              disabled={
                loading ||
                balanceLoading ||
                !numericAmount ||
                numericAmount <= 0 ||
                !activeCurrency ||
                percent <= 0
              }
              onClick={handleBet}
            >
              {loading
                ? "Processing..."
                : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  }